<template>
  <Modal
    v-model:visible="isVisible"
    :header="t('common.addParam', { param: t('shipments.shipments') })"
    class="add-shipment-modal"
    @keypress.enter="onConfirm"
    @keypress.esc="onCancel"
  >
    <p id="instructions">
      {{ t('routeBuilder.addShipmentInstructions') }}
    </p>

    <form id="new-shipment" aria-labelledby="instructions">
      <InputWrapper
        v-slot="{ ariaProps, inputClasses, inputId, invalid }"
        input-id="origin"
        :label="t('orders.origin')"
        :errors="v$.origin.$errors"
        class="origin"
      >
        <LocationQuickSearch
          :id="inputId"
          v-bind="ariaProps"
          v-model="originLocation"
          :class="inputClasses"
          :invalid="invalid"
          append-to="body"
          autofocus
          :placeholder="t('orders.origin')"
          @clear="onClearOrigin"
          @keypress.enter="onEnterInDropdownInput"
        />
      </InputWrapper>

      <AddStopViewEdit
        v-for="(stop, index) in intermediateStops"
        :id="`stop-${index + 2}`"
        :key="stop.sequenceNumber"
        :location="stop.location.toLocation()"
        :stop-type="stop.stopType"
        append-to="body"
        removable
        @remove="onRemoveStop(index)"
      />

      <AddStopViewEdit
        v-if="showNewLocationViewEdit"
        id="new-stop"
        append-to="body"
        edit-mode
        requires-confirmation
        @cancel="showNewLocationViewEdit = false"
        @confirm="onAddStopConfirm"
      />
      <AddButton
        v-else
        mode="tertiary"
        :label="t('shipments.stops')"
        class="align-self-start"
        @click="onAddStop"
      />

      <InputWrapper
        v-slot="{ ariaProps, inputClasses, inputId, invalid }"
        input-id="destination"
        :label="t('orders.destination')"
        :errors="v$.destination.$errors"
        class="destination"
      >
        <LocationQuickSearch
          :id="inputId"
          v-bind="ariaProps"
          v-model="destinationLocation"
          :class="inputClasses"
          :invalid="invalid"
          append-to="body"
          :placeholder="t('orders.destination')"
          @clear="onClearDestination"
          @keypress.enter="onEnterInDropdownInput"
        />
      </InputWrapper>
    </form>

    <template #footer>
      <RygenButton
        mode="tertiary"
        :label="t('common.cancel')"
        @click="onCancel"
      />
      <AddButton
        mode="primary"
        :label="t('shipments.shipments')"
        @click="onConfirm"
      />
    </template>
  </Modal>
</template>

<script lang="ts" setup>
import type { Ref } from 'vue';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { required, useValidations } from '@rygen/rygen-ui/composables';
import { Location, Optional } from '@rygen/rygen-ui/model';

import { EmbeddableLocation, ShipmentStopType } from '@/model';
import {
  AddStopPayload,
  AddStopToShipmentPayload,
  ProposedShipmentStop,
} from '@/model/routeBuilder';

import LocationQuickSearch from '@/components/location/LocationQuickSearch.vue';
import AddStopViewEdit from '@/components/routeBuilder/addStopToShipment/AddStopViewEdit.vue';
import AddButton from '@/components/ui/AddButton.vue';
import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import InputWrapper from '@rygen/rygen-ui/components/form/InputWrapper.vue';
import Modal from '@rygen/rygen-ui/components/ui/Modal.vue';

const isVisible = defineModel('visible', {
  type: Boolean,
  default: () => false,
});

const emit = defineEmits<{
  (event: 'cancel'): void;
  (event: 'confirm', payload: ProposedShipmentStop[]): void;
}>();

const { t } = useI18n();

const originLocation: Ref<Optional<Location>> = ref();
const destinationLocation: Ref<Optional<Location>> = ref();
const intermediateStops: Ref<ProposedShipmentStop[]> = ref([]);
const showNewLocationViewEdit = ref(false);

const internalValue = computed(() => ({
  origin: originLocation.value,
  destination: destinationLocation.value,
}));

const rules = {
  origin: {
    required: required(t('orders.origin')),
  },
  destination: {
    required: required(t('orders.destination')),
  },
};
const { v$, validate } = useValidations(
  internalValue,
  rules,
  'add-shipment-modal',
);

const onEnterInDropdownInput = (event: KeyboardEvent) => {
  // Stop the keyboard event from bubbling up to the popup card so that the
  // popup isn't closed.
  event.stopPropagation();
};

const buildNewStop = (
  location: Location,
  sequenceNumber: number,
  stopType: AddStopToShipmentPayload['stopType'],
) => {
  return new ProposedShipmentStop({
    shipmentIdentifier: -1,
    sequenceNumber,
    stopType,
    location: EmbeddableLocation.fromLocation(location),
  });
};

const onAddStop = () => {
  showNewLocationViewEdit.value = true;
};

const onAddStopConfirm = ({ location, stopType }: AddStopPayload) => {
  const sequenceNumber = intermediateStops.value.length + 3;
  const newStop = new ProposedShipmentStop({
    shipmentIdentifier: -1,
    sequenceNumber,
    stopType,
    location,
  });

  intermediateStops.value.push(newStop);
  showNewLocationViewEdit.value = false;
};

const onRemoveStop = (index: number) => {
  intermediateStops.value.splice(index, 1);
};

const onConfirm = async () => {
  if (!(await validate())) {
    return;
  }

  // validation ensures origin/destination are defined
  const origin = buildNewStop(
    originLocation.value!,
    1,
    ShipmentStopType.PICKUP,
  );
  const destination = buildNewStop(
    destinationLocation.value!,
    intermediateStops.value.length + 2,
    ShipmentStopType.DROP_OFF,
  );

  isVisible.value = false;
  emit('confirm', [origin, ...intermediateStops.value, destination]);
};

const onCancel = () => {
  isVisible.value = false;
  emit('cancel');
};

const onClearOrigin = () => {
  originLocation.value = undefined;
  v$.value.origin.$reset();
};

const onClearDestination = () => {
  destinationLocation.value = undefined;
  v$.value.destination.$reset();
};

watch(isVisible, () => {
  originLocation.value = undefined;
  destinationLocation.value = undefined;
  intermediateStops.value = [];
  v$.value.$reset();
});
</script>

<style>
.add-shipment-modal {
  p#instructions {
    margin-bottom: var(--sp-3);
  }

  form#new-shipment {
    display: flex;
    flex-direction: column;
    gap: var(--gap-1);

    .origin + .add-stop-view-edit:has(.add-stop-view) {
      border-top: var(--divider);
      padding-top: var(--sp-3);
    }

    .add-stop-view-edit {
      .add-stop-edit {
        border-top: var(--divider);
        border-bottom: var(--divider);
        padding-block: var(--sp-3);
      }
    }
  }
}
</style>
